import I18n from 'i18n-js';
import React from 'react';
import classNames from 'classnames';

import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { RCIcon } from '@reverbdotcom/cadence/components';
import { TruckIcon } from '@reverbdotcom/cadence/icons/react';
import { formatAmountCents } from '@reverbdotcom/commons/src/money';

import { CARRIER_CALC_MAX_LENGTH_INCHES, CARRIER_CALC_MAX_WEIGHT_POUNDS } from '../../../../shared/edit_shipping_rate_card_package';
import URLHelpers from '../../../../shared/url_helpers';
import { ShippingMode } from './DomesticShippingCard';

interface IProps {
  shippingMode: ShippingMode;
  active: boolean;
  shopAddressSummary?: string;
  localizedRegionName: string;
  currency: string;
}

const LOCALIZED_PRICE_EXAMPLE_CENTS = 2500;

function ShippingRadioLabel({ shippingMode, active, shopAddressSummary, localizedRegionName, currency }: IProps) {
  return (
    <div className="shipping-radio-label">
      <div className="shipping-radio-label__body">
        <div>
          {I18n.t(`discovery.sellForm.shippingSection.domesticShippingCard.radios.label.${shippingMode}`)}
        </div>
        <div className="shipping-radio-label__sublabel">
          <span>
            {I18n.t(`discovery.sellForm.shippingSection.domesticShippingCard.radios.subLabel.${shippingMode}`, { shopAddressSummary })}
          </span>

          {shippingMode === ShippingMode.CARRIER_CALCULATED && (
            <span className="shipping-radio-label__sublabel__link">
              <CoreLink
                to={URLHelpers.legalPagePath('calculated-shipping-terms-and-conditions')}
                target="_blank"
              >
                {I18n.t('discovery.sellForm.shippingSection.domesticShippingCard.radios.carrierCalcTerms')}
              </CoreLink>
            </span>
          )}
        </div>
      </div>
      <div className="shipping-radio-label__help">
        <div className={classNames(
          'shipping-radio-label__help__background',
          { 'shipping-radio-label__help__background--selected': active },
        )}>
          <div className="shipping-radio-label__help__box shipping-radio-label__help__box--border">
            <div>
              <strong>
                {I18n.t('discovery.sellForm.shippingSection.domesticShippingCard.radios.bestFor')}
              </strong>
            </div>

            <div>
              {I18n.t(`discovery.sellForm.shippingSection.domesticShippingCard.radios.bestForType.${shippingMode}`)}
            </div>

            {shippingMode === ShippingMode.CARRIER_CALCULATED && (
              <div className="shipping-radio-label__explainer__text">
                {I18n.t('discovery.sellForm.shippingSection.domesticShippingCard.radios.explainer.carrierCalculated', {
                  poundsMax: CARRIER_CALC_MAX_WEIGHT_POUNDS,
                  inchesMax: CARRIER_CALC_MAX_LENGTH_INCHES,
                })}
              </div>
            )}
          </div>

          <div className="shipping-radio-label__help__box">
            <div>
              <strong>
                {I18n.t('discovery.sellForm.shippingSection.domesticShippingCard.radios.buyerSees')}
              </strong>
            </div>
            <div className="shipping-radio-label__example__text">
              {shippingMode === ShippingMode.FREE && (
                <div className="shipping-radio-label__example__icon">
                  <RCIcon svgComponent={TruckIcon}/>
                </div>
              )}
              <div>
                {I18n.t(
                  `discovery.sellForm.shippingSection.domesticShippingCard.radios.example.${shippingMode}`,
                  {
                    localizedRegionName,
                    localizedPriceExample: formatAmountCents({ amountCents: LOCALIZED_PRICE_EXAMPLE_CENTS, currency }, { precision: 0 }) })
                }
              </div>
            </div>

            {shippingMode !== ShippingMode.FREE && (
              <div className="shipping-radio-label__explainer__text">
                {I18n.t(`discovery.sellForm.shippingSection.domesticShippingCard.radios.disclaimer.${shippingMode}`)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ShippingRadioLabel;
